import React, { useRef, useState, useLayoutEffect } from "react";

// Extra backing-liner margin around the visible die-cut sticker (inches, per side).
const LINER_MARGIN_IN = 0.25;

const maskStyle = (url) => ({
  WebkitMaskImage: `url(${url})`,
  maskImage: `url(${url})`,
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
});

// True-to-scale kiss-cut: the die-cut sticker footprint (footW x footH, the size
// the customer selected) sits on a backing liner sized 0.25" beyond every edge.
export default function KissView({ stickerSrc, isTransparent, isHolo, footW, footH }) {
  const stageRef = useRef(null);
  const [dim, setDim] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setDim({ w: r.width, h: r.height });
    });
    ro.observe(el);
    const r = el.getBoundingClientRect();
    setDim({ w: r.width, h: r.height });
    return () => ro.disconnect();
  }, []);

  const stickW = Number(footW); // sticker footprint = selected size
  const stickH = Number(footH);
  const linerWin = stickW + 2 * LINER_MARGIN_IN;
  const linerHin = stickH + 2 * LINER_MARGIN_IN;

  const availW = dim.w * 0.82;
  const availH = dim.h * 0.82;
  const ppi = dim.w && stickW > 0 ? Math.min(availW / linerWin, availH / linerHin) : 0;

  const linerW = linerWin * ppi;
  const linerH = linerHin * ppi;
  const stickerW = stickW * ppi;
  const stickerH = stickH * ppi;
  const pad = LINER_MARGIN_IN * ppi;

  return (
    <div ref={stageRef} data-testid="sticker-stage" className="relative w-full h-[460px] sm:h-[540px] rounded-2xl overflow-hidden flex items-center justify-center macbook-surface">
      {ppi > 0 && (
        <div
          className="relative backing-liner rounded-lg nb-border nb-shadow-sm"
          data-testid="kiss-liner"
          style={{ width: `${linerW}px`, height: `${linerH}px` }}
        >
          <div className="absolute inset-0 rounded-lg opacity-30 pointer-events-none" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.4), rgba(0,0,0,0))" }} />
          <div className="absolute" style={{ left: `${pad}px`, top: `${pad}px`, width: `${stickerW}px`, height: `${stickerH}px` }}>
            <img
              src={stickerSrc}
              alt="Sticker preview"
              data-testid="sticker-preview-img"
              className="w-full h-full object-contain drop-shadow-[1px_2px_2px_rgba(0,0,0,0.22)]"
              style={{ mixBlendMode: isTransparent ? "multiply" : "normal" }}
            />
            {isHolo && (
              <div className="holo-sheen absolute inset-0 pointer-events-none" style={{ ...maskStyle(stickerSrc), mixBlendMode: "screen", opacity: 0.6 }} />
            )}
          </div>
        </div>
      )}
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur nb-border rounded-full px-3 py-1 text-xs font-head font-bold">
        {isHolo ? "Holographic foil" : isTransparent ? "See-through vinyl" : "White vinyl"} · on backing liner
      </div>
    </div>
  );
}
